import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useSound from 'use-sound';
import moment from 'moment-timezone';
import { Icon } from '@iconify/react';
import { Button } from 'reactstrap';
import _ from 'lodash';
import ringSound from '../assets/sounds/ring.mp3';
import {
  alert,
  getReferralByCode,
  sendNotiByRefCode,
  setListsNotification,
  updateRealtime,
  referralUpdate,
  addCallQueue
} from '../actions';
import { database, firebase } from '../helpers/firebase';
import Calling from './Modal/Calling';
import CallingWaiting from './Modal/CallingWaiting';
import RequestCallNowNotOnline from './Modal/RequestCallNowNotOnline';
import HowToUse from './Modal/HowToUse';
import SignalDisconnected from './Modal/SignalDisconnected';
import PrepareVideo from './PrepareVideo';
import CallbackQueue from './CallbackQueue';
import {
  encrypt,
  getDateTimeDifference,
  isBrowserSupport,
  isAndroid
} from '../helpers/utilities';

let intervalId = null;
const SOUND_ENABLED_STORAGE_KEY = 'incoming-call-sound-enabled';

const isActiveTelemedRoute = () => {
  const { pathname } = window.location;
  return pathname.startsWith('/telemed/') && !pathname.startsWith('/telemed/doctor/');
};

const InitialComponent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [process, setProcess] = useState('load-first');
  const [callingData, setCallingData] = useState();
  const [pendingCallingRequest, setPendingCallingRequest] = useState(null);
  const [incomingCallStatus, setIncomingCallStatus] = useState('ready');
  const [soundEnabled, setSoundEnabled] = useState(() => window.sessionStorage.getItem(SOUND_ENABLED_STORAGE_KEY) === 'true');
  const [isOpen, setIsOpen] = useState(false);
  const [initFirebase, setInitFirebase] = useState(false);
  const [isUseCallingWaiting, setIsUseCallingWaiting] = useState(false);
  const [callingWaitingData, setCallingWaitingData] = useState(null);
  const [dataRequestCallNow, setDataRequestCallNow] = useState();
  const [isOpenRequestCallNow, setIsOpenRequestCallNow] = useState(false);
  const [isOpenSignal, setIsOpenSignal] = useState(false);
  const [isOpenFirstLogin, setOpenFirstLogin] = useState(false);

  const theme = useSelector(state => state.whitelabel.theme);
  const auth = useSelector(selector => selector.auth);
  const { profile } = auth;
  const referral = useSelector(selector => selector.referral);
  const realtime = useSelector(selector => selector.realtime);
  const [dataBookings, setDataBookings] = useState([]);
  const [isOpenPrepareVideo, setOpenPrepareVideo] = useState(false);
  const [dataBooking, setDataBooking] = useState({});
  const [checkInterval, setCheckInterval] = useState(1);
  const [android] = useState(isAndroid());
  const [isBrowser] = useState(isBrowserSupport());

  useEffect(() => {
    if (_.has(isBrowser, 'browser') && (isBrowser.browser === 'line' || isBrowser.browser === 'liff')) {
      const currentURL = window.location.href;
      let urlAndroidRedirect = currentURL;
      const androidUrls = urlAndroidRedirect.split('#');
      urlAndroidRedirect = `${androidUrls[0]}?openExternalBrowser=1#${androidUrls[1]}`;
      const androidUrlsErr = urlAndroidRedirect;
      if (android) {
        try {
          urlAndroidRedirect = urlAndroidRedirect.replace('https://', '//');
          window.location.href = `intent:${urlAndroidRedirect}#Intent;scheme=https;package=com.android.chrome;end`;
        } catch (error) {
          window.location.href = androidUrlsErr;
        }
      } else {
        window.location.href = urlAndroidRedirect;
      }
    }
  }, [isBrowser, android]);

  const signalDisconnectedCallback = () => {
    dispatch(updateRealtime('reconnect-signal', true));
  };

  const toggleRequestCallNow = input => {
    if (input) {
      setDataRequestCallNow(input);
    }
    setIsOpenRequestCallNow(!isOpenRequestCallNow);
  };

  const toggleFirstLogin = () => {
    setOpenFirstLogin(!isOpenFirstLogin);
  };

  const [play, { stop }] = useSound(ringSound, {
    interrupt: true,
    loop: true
  });

  const stopIncomingCall = useCallback(() => {
    stop();
    setIsOpen(false);
    setCallingData(undefined);
    setPendingCallingRequest(null);
    setIncomingCallStatus('ready');
    setProcess('');
  }, [stop]);

  const enableSound = useCallback(() => {
    window.sessionStorage.setItem(SOUND_ENABLED_STORAGE_KEY, 'true');
    setSoundEnabled(true);
    play();
    if (!isOpen) {
      window.setTimeout(stop, 150);
    }
  }, [isOpen, play, stop]);

  const toggle = (val = !isOpen) => {
    setIsOpen(val);
  };

  const toggleWaiting = () => {
    setIsUseCallingWaiting(!isUseCallingWaiting);
  };

  const queueIncomingCall = useCallback(request => {
    if (!request?.refCode || !request?.patCode) return;
    dispatch(addCallQueue({
      refCode: request.refCode,
      patCode: request.patCode,
      receivedAt: Date.now(),
      patientFirstName: request.patientFirstName,
      patientLastName: request.patientLastName,
      patientMobile: request.patientMobile,
      createByApp: request.createByApp,
      information: request.information
    }));
    dispatch(updateRealtime('calling-busy', true, {
      refCode: request.refCode,
      patCode: request.patCode
    }));
    alert({
      type: 'warning',
      title: t('Incoming Call'),
      message: `#${request.refCode}\n${t('The call was added to the callback queue')}`,
      icon: 'ion:information-circle',
      datetime: new Date(),
      fill: false
    });
  }, [dispatch, t]);

  const getRefData = useCallback(data => {
    setProcess('referral-fetch');
    setIncomingCallStatus('loading');
    dispatch(getReferralByCode(data.refCode));
  }, [dispatch]);

  const retryIncomingCall = useCallback(() => {
    if (pendingCallingRequest) getRefData(pendingCallingRequest);
  }, [getRefData, pendingCallingRequest]);

  const callingCallbackWaiting = (type, data) => {
    if (type === 'confirm') {
      if (data?.information?.doctor) {
        setDataRequestCallNow(data);
        setIsOpenRequestCallNow(true);
      }
      setIsUseCallingWaiting(false);
      setCallingWaitingData(null);
    } else {
      setIsUseCallingWaiting(false);
      setCallingWaitingData(null);
    }
  };

  const callingCallback = (type, patCode) => {
    if (type === 'accept') {
      dispatch(updateRealtime('calling-accept', true, { patCode }));
      setTimeout(() => {
        dispatch(sendNotiByRefCode(callingData.refCode, 'EVENT_D001'));
      }, 300);
      stop();
      stopIncomingCall();
      const dataSet = {
        refCode: callingData.refCode,
        patCode: callingData.patCode,
        createByApp: callingData.createByApp,
        patientFirstName: callingData.patientFirstName,
        patientLastName: callingData.patientLastName,
        timeSlotRange: callingData.information.timeSlotRange,
        patientMobile: callingData.patientMobile,
        join: true
      };
      const token = encodeURIComponent(encrypt(dataSet).replace(/\//g, '---'));
      setTimeout(() => {
        navigate(`/telemed/${token}`);
      }, 500);
    } else {
      setTimeout(() => {
        dispatch(sendNotiByRefCode(callingData.refCode, 'EVENT_D003'));
      }, 300);
      dispatch(updateRealtime('reject-calling', true, { patCode, refCode: callingData.refCode }));
      stopIncomingCall();
    }
  };

  const renderAlert = useCallback(value => {
    const timezone = localStorage.getItem('timezone') || moment.tz.guess();
    const refCode = value?.activity?.refCode || value?.refCode || '';
    const data = {
      title: value?.title,
      message: typeof value?.message === 'string' ? value?.message : value?.message?.join(', '),
      icon: 'ion:information-circle',
      link: `/consult?data=${refCode}`,
      datetime: moment.utc(value.createdAt).tz(timezone),
      fill: false,
      type: 'warning'
    };
    if (value?.status) {
      if (value.status === 'NEW_BOOKING') {
        data.title = t('New appointments');
        data.icon = 'fluent:calendar-checkmark-16-filled';
        data.type = 'success';
        dispatch(updateRealtime('referral-update', true));
      } else if (value.status === 'CHANGE_INFORMATION') {
        data.title = t('Change information appointment');
        data.message = `#${refCode}`;
        data.icon = 'ion:information-circle';
        data.type = 'warning';
        dispatch(updateRealtime('referral-update', true, { refCode }));
      } else if (value.status === 'CONFIRM_CHANGE_BOOKING') {
        data.title = t('Patient confirm change appointment');
        data.message = `#${refCode}`;
        data.icon = 'fluent:calendar-checkmark-16-filled';
        dispatch(updateRealtime('referral-update', true, { refCode }));
      } else if (value.status === 'CHANGE_BOOKING') {
        data.title = t('Patient wants to change the appointment');
        data.message = `#${refCode}`;
        data.icon = 'fluent:calendar-arrow-right-16-filled';
        data.type = 'warning';
        dispatch(updateRealtime('referral-update', true, { refCode }));
      } else if (value.status === 'CANCEL_BOOKING') {
        data.title = t('The consultation appointment was canceled');
        data.message = `#${refCode}`;
        data.icon = 'fluent:calendar-arrow-right-16-filled';
        data.type = 'danger';
        dispatch(updateRealtime('referral-update', true, { refCode }));
      } else if (value.status === 'CONFIRM_REQUEST_CALL_NOW') {
        data.title = t('Patient accept request call now');
        data.message = `#${refCode}`;
        data.type = 'success';
        dispatch(updateRealtime('referral-update', true, { refCode }));
      } else if (value.status === 'REJECT_REQUEST_CALL_NOW') {
        data.title = t('Patient reject request call now');
        data.message = `#${refCode}`;
        data.type = 'danger';
      } else if (value.status === 'CONFIRM_REQUEST_HEALTH_RECORD') {
        data.title = t('Patient accept request for access to health information');
        data.message = `#${refCode}`;
        data.type = 'success';
        dispatch(updateRealtime('referral-update', true, { refCode }));
      } else if (value.status === 'REJECT_REQUEST_HEALTH_RECORD') {
        data.title = t('Patient reject request for access to health information');
        data.message = `#${refCode}`;
        data.type = 'danger';
      } else if (value.status === 'CALL_NOW') {
        data.title = t('The patient calls you now');
        data.message = `#${refCode}`;
        data.type = 'warning';
      }
    }
    alert(data);
  }, [dispatch, t, updateRealtime]);

  useEffect(() => {
    if (profile?.docCode && !initFirebase) {
      setInitFirebase(true);
      const notifications = firebase.query(firebase.ref(database, 'notifications'), firebase.orderByChild('to_code'), firebase.equalTo(profile.docCode), firebase.limitToLast(1));
      firebase.onChildAdded(notifications, snapshot => {
        const value = snapshot.val();
        if (value.readBy.includes('NO-READ')) {
          renderAlert(value);
          value.readBy = [profile.docCode];
          value.updatedAt = moment().format('YYYY-MM-DD HH:mm:ss');
          firebase.update(firebase.ref(database, `notifications/${snapshot.key}`), value);
        }
      });
      const lists = firebase.query(firebase.ref(database, 'notifications'), firebase.orderByChild('to_code'), firebase.equalTo(profile.docCode), firebase.limitToLast(500));
      const newLists = [];
      firebase.onChildAdded(lists, snapshot => {
        const newValue = snapshot.val();
        if (newValue?.status) {
          newLists.push({
            value: newValue,
            key: snapshot.key
          });
          dispatch(setListsNotification(newLists));
        }
      });
    }
  }, [profile, initFirebase, setInitFirebase]);

  const toVideoRoom = input => {
    const dataSet = {
      refCode: input.refCode,
      patCode: input.patCode,
      createByApp: input.createByApp,
      patientFirstName: input.patientFirstName,
      patientLastName: input.patientLastName,
      timeSlotRange: 15,
      patientMobile: input.patientMobile,
      isCalling: false,
      join: true
    };
    const token = encodeURIComponent(encrypt(dataSet).replace(/\//g, '---'));
    navigate(`/telemed/${token}`);
  };

  useEffect(() => {
    if (realtime.name === 'calling-request' && realtime.status) {
      if (realtime.data && realtime.data.refCode) {
        dispatch(updateRealtime('calling-request', false));
        if (isActiveTelemedRoute()) {
          queueIncomingCall(realtime.data);
        } else if (!isOpen) {
          setCallingData(realtime.data);
          setPendingCallingRequest(realtime.data);
          setIncomingCallStatus('loading');
          toggle(true);
          if (soundEnabled) play();
          getRefData(realtime.data);
        } else if (realtime?.data?.refCode !== callingData?.refCode && realtime?.data?.patCode) {
          dispatch(updateRealtime('calling-busy', true, { refCode: realtime.data.refCode, patCode: realtime.data.patCode }));
        }
      }
    }
    if (realtime.name === 'calling-cancel' && realtime.status) {
      stopIncomingCall();
      dispatch(updateRealtime('calling-cancel', false));
    }
    if (realtime.name === 'calling-timeout' && realtime.status) {
      stopIncomingCall();
      dispatch(updateRealtime('calling-timeout', false));
    }
    if (realtime.name === 'connect-signal' && !realtime.status && realtime?.data?.refCode) {
      dispatch(updateRealtime('connect-telemed', true));
      const dataEncrypt = { ...realtime.data };
      delete dataEncrypt.referral;
      const token = encodeURIComponent(encrypt(dataEncrypt).replace(/\//g, '---'));
      navigate(`/telemed/${token}`);
    }
    if (realtime.name === 'calling-use-waiting-call' && realtime.status) {
      dispatch(updateRealtime('calling-use-waiting-call', false));
      setCallingWaitingData(realtime.data.referral);
      setIsUseCallingWaiting(true);
    }
    if (realtime.name === 'connect-signal-easycall-send' && realtime.status) {
      dispatch(updateRealtime('connect-signal-easycall-send', false));
      toVideoRoom(realtime.data);
    }
  }, [realtime, isOpen, callingData, dispatch, getRefData, queueIncomingCall, stopIncomingCall, soundEnabled, play]);

  useEffect(() => {
    if (process === 'referral-fetch' && referral.status) {
      setProcess('');
      if (isActiveTelemedRoute() && pendingCallingRequest) {
        queueIncomingCall(pendingCallingRequest);
        stopIncomingCall();
      } else if (referral.status === 'success' && referral.detail) {
        setIncomingCallStatus('ready');
        setCallingData(referral.detail);
        setPendingCallingRequest(null);
      } else {
        setIncomingCallStatus('failed');
        alert({
          type: 'warning',
          title: t('Incoming Call'),
          message: t(referral.message || 'Unable to load patient information'),
          icon: 'ion:alert-circle',
          fill: false
        });
      }
    }
  }, [referral, process, pendingCallingRequest, queueIncomingCall, stopIncomingCall, t]);

  useEffect(() => () => stop(), [stop]);

  useEffect(() => {
    if (realtime.signalStatus === 'disconnected') {
      const currentPath = window.location.pathname;
      if (currentPath.search('/telemed/') < 0) {
        setIsOpenSignal(true);
      }
    }
  }, [realtime]);

  useEffect(() => {
    if (checkInterval && !_.has(dataBooking, 'pendingInformation.visitDate') && _.has(dataBooking, 'refCode') && _.has(dataBooking, 'status') && _.has(dataBooking, 'information.visitDate') && _.has(dataBooking, 'information.visitTime') && (!_.has(dataBooking, 'doctorRecivedCall') || !dataBooking.doctorRecivedCall) && realtime && dataBooking.status === 'paid') {
      let timeSlotRange = 15;
      if (_.has(dataBooking, 'information.timeSlotRange')) {
        timeSlotRange = dataBooking.information.timeSlotRange;
      }
      const timeMilliseconds = timeSlotRange * 60 * 1000;
      const currentDate = moment().format('YYYY-MM-DD HH:mm:ss');
      const consultDate = moment(`${dataBooking.information.visitDate} ${dataBooking.information.visitTime}`).format('YYYY-MM-DD HH:mm:ss');
      const differenceInMilliseconds = getDateTimeDifference(currentDate, consultDate);
      if (!isOpenPrepareVideo && differenceInMilliseconds <= 0 && (differenceInMilliseconds + timeMilliseconds) > 0) {
        setOpenPrepareVideo(true);
        clearInterval(intervalId);
      } else if (isOpenPrepareVideo && differenceInMilliseconds <= 0 && (differenceInMilliseconds + timeMilliseconds) < 0) {
        setOpenPrepareVideo(false);
        clearInterval(intervalId);
      }
    }
  }, [dataBooking, checkInterval]);

  useEffect(() => {
    let loop = 1;
    intervalId = setInterval(() => {
      loop += 1;
      setCheckInterval(loop);
    }, 1000 * 60);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (dataBookings.length > 0) {
      const currentDate = moment().format('YYYY-MM-DD');
      const dataCurrent = dataBookings.filter(o => o.information.visitDate === currentDate);
      const data = _.orderBy(dataCurrent, ['information.visitDate', 'information.visitTime'], ['asc', 'asc']);
      if (data.length > 0) {
        setDataBooking(data[0]);
      } else {
        setDataBooking(dataBookings[0]);
      }
    }
  }, [dataBookings]);

  useEffect(() => {
    let dataBookingList = [];
    if (referral.data.length > 0) {
      dataBookingList = referral.data.filter(o => o.status === 'paid' && (_.has(o, 'doctorRecivedCall') || !o.doctorRecivedCall));
    }
    if (referral.lastData.length > 0) {
      dataBookingList = _.concat(dataBookingList, referral.lastData.filter(o => o.status === 'paid' && (_.has(o, 'doctorRecivedCall') || !o.doctorRecivedCall)));
    }
    if (dataBookingList.length > 0) {
      setDataBookings(_.uniqBy(dataBookingList, 'refCode'));
    } else {
      setDataBookings([]);
    }
  }, [referral]);

  const callNow = useCallback(input => {
    const useNewChangeToVideo = true;
    dispatch(referralUpdate(input.refCode, {
      feedback_comment: 'null',
      feedback_rate_start: 'null'
    }));
    if (useNewChangeToVideo) {
      toVideoRoom(input);
    } else {
      setTimeout(() => {
        dispatch(updateRealtime('connect-signal', true, {
          refCode: input.refCode,
          patCode: input.patCode,
          createByApp: input.createByApp,
          patientFirstName: input.patientFirstName,
          patientLastName: input.patientLastName,
          patientImage: input.mapImagePatient,
          timeSlotRange: input.information.timeSlotRange,
          patientMobile: input.patientMobile,
          createByAppName: input.createByAppName,
          useWeb: input.useWeb,
          shortLinkForComplete: input.shortLinkForComplete,
          referral: input,
          callFromDoctor: true
        }));
      }, 300);
    }
  }, [realtime]);

  const callbackPrepareVideo = data => {
    callNow(data);
  };

  const callbackPrepareVideoClose = data => {
    setOpenPrepareVideo(false);
    dispatch(referralUpdate(data.refCode, {
      doctorRecivedCall: true
    }));
  };

  return (
    <>
      {callingData && (
        <Calling
          isOpen={isOpen}
          toggle={stopIncomingCall}
          data={callingData}
          status={incomingCallStatus}
          soundEnabled={soundEnabled}
          callBack={callingCallback}
          onEnableSound={enableSound}
          onRetry={retryIncomingCall}
        />
      )}
      {profile?.docCode && !soundEnabled && !isOpen && (
        <div className="incoming-call-sound-banner">
          <span>{t('Enable incoming call sound to avoid missing calls')}</span>
          <Button color="secondary" size="sm" onClick={enableSound}>{t('Enable sound')}</Button>
        </div>
      )}
      <CallbackQueue />
      {(isOpenSignal) && (
        <SignalDisconnected isOpen={isOpenSignal} data={`Realtime is ${realtime.signalStatus}`} callBack={signalDisconnectedCallback} />
      )}
      {isUseCallingWaiting && (
        <CallingWaiting isOpen={isUseCallingWaiting} toggle={toggleWaiting} data={callingWaitingData} callBack={callingCallbackWaiting} />
      )}
      {dataRequestCallNow && dataRequestCallNow.refCode && (
        <RequestCallNowNotOnline isOpen={isOpenRequestCallNow} toggle={toggleRequestCallNow} data={dataRequestCallNow} />
      )}
      {profile?.docCode && isOpenFirstLogin && (
        <HowToUse docCode={profile?.docCode} isOpen={isOpenFirstLogin} toggle={toggleFirstLogin} />
      )}
      {profile?.docCode && window.location.pathname === '/' && (!_.has(theme, 'companyCode') || theme.companyCode === '' || theme.companyCode === 'null') && (
        <div className="button-fixed-bottom" aria-hidden="true" onClick={toggleFirstLogin.bind(this)}>
          <Icon icon="mdi:youtube" height="32px" className="cursor-pointer" />
        </div>
      )}
      {((false) && profile?.docCode && _.has(dataBooking, 'refCode') && isOpenPrepareVideo && window.location.pathname.search('/telemed/') < 0) && (
        <PrepareVideo data={dataBooking} callback={callbackPrepareVideo} callbackClose={callbackPrepareVideoClose} />
      )}
    </>
  );
};

export default InitialComponent;
